/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * One outbox entry.
 *
 * The message itself leads — the detail endpoint returns bodyHtml/bodyText (only
 * the list projection strips them), and the previous version showed fifteen
 * metadata fields and never the mail you clicked through to read.
 *
 * The body renders inside a sandboxed iframe rather than dangerouslySetInnerHTML:
 * arbitrary email HTML carries its own <style>, which would otherwise leak into
 * the app, and the sandbox neutralises scripts in anything not sanitised upstream.
 *
 * Delivery is shown as a timeline because queued → scheduled → attempts →
 * sent/failed is one story, not five unrelated rows.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PageHeader from '../../components/common/page-header';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { extractErrorMessage } from '../../utils/error-handler';
import { formatUtcInIst, parseUtcInstant } from '../../utils/timezone.util';
import { emailService, type EmailStatus, type OutboxEntry } from '../../services/email.service';
import { PageSkeleton } from '../../components/common/panel';

const STATUS_SEVERITY: Record<EmailStatus, 'success' | 'info' | 'warning' | 'danger' | 'secondary'> = {
  SENT: 'success',
  SENDING: 'info',
  QUEUED: 'warning',
  FAILED: 'danger',
  CANCELLED: 'secondary',
};

const formatDate = (value?: string | null) =>
  value
    ? formatUtcInIst(value, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

const humanGap = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

/** Small label/value row for the technical panel. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[92px_1fr] items-start gap-3 py-1.5">
      <dt className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </dt>
      <dd className="m-0 min-w-0 break-words text-[12.5px] text-gray-900 dark:text-gray-100">
        {children}
      </dd>
    </div>
  );
}

/** One node on the delivery timeline. */
function Step({
  icon, tone, title, when, note, last,
}: {
  icon: string; tone: 'done' | 'pending' | 'fail'; title: string;
  when?: string | null; note?: string; last?: boolean;
}) {
  const ring = {
    done: 'bg-emerald-500 text-white',
    pending: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    fail: 'bg-rose-600 text-white',
  }[tone];
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {!last && <span className="absolute left-[11px] top-6 h-[calc(100%-1.5rem)] w-px bg-gray-200 dark:bg-gray-700" />}
      <span className={`z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ${ring}`}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.65rem' }} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold text-ink">{title}</span>
        {when && <span className="block tabular-nums text-[11.5px] text-ink-muted">{when}</span>}
        {note && <span className="block text-[11px] text-ink-muted">{note}</span>}
      </span>
    </li>
  );
}

export default function EmailOutboxView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const toast = useRef<any>(null);

  const [entry, setEntry] = useState<OutboxEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'html' | 'text'>('html');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setEntry(await emailService.getEntry(id));
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not load email',
        detail: extractErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Back must return where you came from, not to a fixed path.
   *
   * The Outbox is usually reached as the Delivery tab of /notifications, but
   * opening a message routes to /email/outbox/:id — a standalone page. Sending
   * Back to /email/outbox therefore dropped you on a list that has no sidebar
   * entry, stranded outside the tab group you started in.
   *
   * React Router tracks its own position in `history.state.idx`; anything above
   * zero means there is an in-app entry to return to. A cold load (bookmark,
   * refresh, pasted link) has none, so fall back to the list.
   */
  const goBack = useCallback(() => {
    const idx = (window.history.state as any)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/email/outbox');
  }, [navigate]);

  const act = async (fn: () => Promise<unknown>, summary: string) => {
    setBusy(true);
    try {
      await fn();
      toast.current?.show({ severity: 'success', summary });
      await load();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Action failed',
        detail: extractErrorMessage(error),
      });
    } finally {
      setBusy(false);
    }
  };

  const latency = useMemo(() => {
    const a = parseUtcInstant(entry?.createdAt);
    const b = parseUtcInstant(entry?.sentAt);
    return a && b ? humanGap(b.getTime() - a.getTime()) : null;
  }, [entry]);

  /** Wrap the stored HTML so it renders on a white page at a sane base size. */
  const srcDoc = useMemo(() => {
    if (!entry?.bodyHtml) return null;
    return `<!doctype html><html><head><meta charset="utf-8">
      <style>html,body{margin:0;padding:16px;background:#fff;color:#18181b;
        font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif}
        img{max-width:100%;height:auto}a{color:#4f46e5}</style></head>
      <body>${entry.bodyHtml}</body></html>`;
  }, [entry]);

  const hasHtml = !!entry?.bodyHtml;
  const hasText = !!entry?.bodyText;

  return (
    <PermissionGuard permission="NOTIFICATION_MASTER.UPDATE" resourceName="Mail">
      <Toast ref={toast} position="top-right" />
      <div className="p-6" data-testid="SCR-EmailOutbox-View">
        <PageHeader
          icon="pi pi-send"
          title={entry?.subject || 'Email'}
          subtitle={entry ? `${entry.source || 'manual'} · queued ${formatDate(entry.createdAt) || '—'}` : undefined}
          testId="EmailOutboxView.Header"
          actions={
            <div className="flex items-center gap-2">
              <Button label="Back" icon="pi pi-arrow-left" variant="text" size="small"
                      onClick={goBack} testId="EmailOutboxView.Button.Back" />
              {entry?.status === 'QUEUED' && (
                <Button label="Cancel" icon="pi pi-times" variant="secondary" size="small" disabled={busy}
                        onClick={() => void act(() => emailService.cancel(entry.id), 'Cancelled')}
                        testId="EmailOutboxView.Button.Cancel" />
              )}
              {(entry?.status === 'FAILED' || entry?.status === 'SENT') && (
                <Button label="Resend" icon="pi pi-replay" size="small" disabled={busy}
                        onClick={() => void act(() => emailService.resend(entry.id), 'Re-queued')}
                        testId="EmailOutboxView.Button.Resend" />
              )}
            </div>
          }
        />

        {loading && <PageSkeleton panels={2} className="" />}

        {!loading && entry && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            {/* ── the message ─────────────────────────────────── */}
            <div className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 p-4 dark:border-gray-700">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="m-0 text-base font-bold leading-snug text-ink">
                    {entry.subject || <span className="italic text-gray-400">No subject</span>}
                  </h2>
                  <Tag value={entry.status} severity={STATUS_SEVERITY[entry.status] ?? 'secondary'} />
                </div>

                <dl className="mt-3 space-y-1">
                  <div className="flex gap-2 text-[12.5px]">
                    <dt className="w-9 shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">To</dt>
                    <dd className="m-0 break-words text-gray-900 dark:text-gray-100">
                      {(entry.to || []).join(', ') || '—'}
                    </dd>
                  </div>
                  {entry.cc?.length > 0 && (
                    <div className="flex gap-2 text-[12.5px]">
                      <dt className="w-9 shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Cc</dt>
                      <dd className="m-0 break-words text-gray-900 dark:text-gray-100">{entry.cc.join(', ')}</dd>
                    </div>
                  )}
                  {entry.bcc?.length > 0 && (
                    <div className="flex gap-2 text-[12.5px]">
                      <dt className="w-9 shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Bcc</dt>
                      <dd className="m-0 break-words text-gray-900 dark:text-gray-100">
                        {entry.bcc.join(', ')}
                        <span className="ml-2 text-[11px] text-gray-400">
                          hidden from recipients — sent via the SMTP envelope
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* body */}
              {(hasHtml || hasText) ? (
                <>
                  {hasHtml && hasText && (
                    <div className="flex gap-1 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Button label="HTML" size="small" variant={view === 'html' ? 'primary' : 'outlined'}
                              onClick={() => setView('html')} testId="EmailOutboxView.Button.Html" />
                      <Button label="Plain text" size="small" variant={view === 'text' ? 'primary' : 'outlined'}
                              onClick={() => setView('text')} testId="EmailOutboxView.Button.Text" />
                    </div>
                  )}
                  {view === 'html' && hasHtml ? (
                    <iframe
                      title="Email body"
                      srcDoc={srcDoc || ''}
                      sandbox=""
                      className="h-[520px] w-full border-0 bg-white"
                    />
                  ) : (
                    <pre className="m-0 max-h-[520px] overflow-auto whitespace-pre-wrap bg-white p-4 font-mono text-[12.5px] leading-relaxed text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                      {entry.bodyText || '—'}
                    </pre>
                  )}
                </>
              ) : (
                <div className="p-10 text-center text-sm text-ink-muted">
                  <i className="pi pi-file mb-2 block text-2xl opacity-40" />
                  This message has no stored body.
                </div>
              )}

              {entry.attachments?.length > 0 && (
                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    {entry.attachments.length} attachment{entry.attachments.length > 1 ? 's' : ''}
                  </div>
                  <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                    {entry.attachments.map((a, i) => (
                      <li key={`${a.filename}-${i}`}
                          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-[12px] dark:border-gray-700 dark:bg-gray-900/40">
                        <i className={`${a.isInline ? 'pi pi-image' : 'pi pi-paperclip'} text-gray-400`} />
                        <span className="text-gray-900 dark:text-gray-100">{a.filename || 'unnamed'}</span>
                        <span className="text-gray-400">{a.mimeType}</span>
                        {a.isInline && <span className="text-[10px] font-semibold uppercase text-blue-600">inline</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* ── delivery ────────────────────────────────────── */}
            <div className="flex min-w-0 flex-col gap-4">
              {entry.status === 'FAILED' && entry.lastError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/50 dark:bg-rose-950/40">
                  <div className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                    Why it failed
                  </div>
                  <pre className="m-0 overflow-x-auto whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed text-rose-800 dark:text-rose-200">
                    {entry.lastError}
                  </pre>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Delivery
                </div>
                <ol className="m-0 list-none p-0">
                  <Step icon="pi-inbox" tone="done" title="Queued" when={formatDate(entry.createdAt)} />
                  {entry.scheduledAt && (
                    <Step icon="pi-clock" tone="done" title="Scheduled" when={formatDate(entry.scheduledAt)} />
                  )}
                  {entry.attempts > 1 && (
                    <Step icon="pi-replay" tone={entry.status === 'FAILED' ? 'fail' : 'done'}
                          title={`${entry.attempts} attempts`}
                          note={entry.nextAttemptAt ? `next ${formatDate(entry.nextAttemptAt)}` : undefined} />
                  )}
                  {entry.status === 'SENT' ? (
                    <Step icon="pi-check" tone="done" title="Accepted by provider" last
                          when={formatDate(entry.sentAt)}
                          note={latency ? `${latency} after queueing` : undefined} />
                  ) : entry.status === 'FAILED' ? (
                    <Step icon="pi-times" tone="fail" title="Failed" last when={formatDate(entry.updatedAt)} />
                  ) : entry.status === 'CANCELLED' ? (
                    <Step icon="pi-ban" tone="pending" title="Cancelled" last when={formatDate(entry.updatedAt)} />
                  ) : (
                    <Step icon="pi-spin pi-spinner" tone="pending" title="Awaiting delivery" last
                          note={entry.nextAttemptAt ? `next attempt ${formatDate(entry.nextAttemptAt)}` : 'in the send queue'} />
                  )}
                </ol>

                {/* SMTP acknowledges submission, not receipt — say so where the
                    claim is made rather than as a field of its own. */}
                <p className="m-0 mt-3 border-t border-gray-100 pt-3 text-[11px] leading-relaxed text-gray-400 dark:border-gray-700 dark:text-gray-500">
                  There is no delivered state by design. SMTP confirms the provider
                  accepted the message, never that it reached the inbox.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Technical
                </div>
                <dl className="m-0 divide-y divide-gray-100 dark:divide-gray-700">
                  <Fact label="Source">{entry.source || '—'}</Fact>
                  <Fact label="Provider">{entry.provider || '—'}</Fact>
                  <Fact label="Message id">
                    <span className="break-all font-mono text-[11px]">{entry.providerMessageId || '—'}</span>
                  </Fact>
                  {entry.notificationId && (
                    <Fact label="Notification">
                      <span className="break-all font-mono text-[11px]">{entry.notificationId}</span>
                    </Fact>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
}
